import type {
  ColumnDef,
  Leaves,
  LedgerUpdateTypeEnum,
  Position,
  PositionAssetTypeEnum,
  SubAccountTypeEnum,
  TabProps,
} from '@talos/kyoko';
import type { UnifiedPosition } from './Unified/UnifiedPosition';
import type { PositionsSourceFilterMode } from './Unified/unifiedSourceFilter';

export interface BasePositionsTabProps extends TabProps {
  tag?: string;
  defaultFilter?: PositionsTableFilter;
  defaultColumns: PositionColumn[];
  showZeroBalances: boolean;
}

export interface DerivativesPositionsTabProps extends BasePositionsTabProps {
  tabType: 'Derivatives';
}

export interface UnifiedPositionsTabProps extends TabProps {
  defaultColumns: UnifiedPositionColumn[];
  tabType: 'Unified';
  includeCash?: boolean;
  tag?: string;
  defaultFilter?: PositionsTableFilter;
  showZeroBalances: boolean;
}

export interface SubAccountPositionsTabProps extends BasePositionsTabProps {
  tabType: 'Sub Accounts';
  includeCash?: boolean;
  hierarchical?: boolean;
}

export interface PredictionsPositionsTabProps extends BasePositionsTabProps {
  tabType: 'Predictions';
}

export type PositionsTabProps =
  | SubAccountPositionsTabProps
  | DerivativesPositionsTabProps
  | UnifiedPositionsTabProps
  | PredictionsPositionsTabProps;

export type PositionsTabType = 'Derivatives' | 'Sub Accounts' | 'Unified' | 'Predictions';

export type IncludeCashPositionsTabProps = Extract<PositionsTabProps, { tabType: 'Unified' | 'Sub Accounts' }>;

export function isIncludeCashPositionsTab(tab: PositionsTabProps): tab is IncludeCashPositionsTabProps {
  return tab.tabType === 'Unified' || tab.tabType === 'Sub Accounts';
}

// The UnifiedPosition stuff here is adding a lot of complexity. The backend will do all this for us instead asap and we can remove all this shit.
export type ExtraUnifiedPositionColumns = keyof Pick<UnifiedPosition, 'market' | 'marketAccountGroup' | 'underlying'>;
export type ExtraPositionColumns =
  | 'expiry'
  | 'instrument'
  | 'SubAccountType'
  | 'underlying'
  | 'predictionCategory'
  | 'predictionSeries'
  | 'predictionEvent';

export type UnifiedPositionColumn =
  | Leaves<UnifiedPosition>
  | ExtraPositionColumns
  | ExtraUnifiedPositionColumns
  | Partial<ColumnDef<UnifiedPosition>>;
export type PositionColumn = Leaves<Position> | ExtraPositionColumns | Partial<ColumnDef<Position>>;

export interface PositionsTableFilter {
  Source?: PositionsSourceFilterMode;
  AssetTypes?: PositionAssetTypeEnum[];
  SubAccounts?: string[];
  Symbols?: string[];
  SubAccountTypes?: SubAccountTypeEnum[];
  Expiries?: string[];
  MarketAccounts?: string[];
  Markets?: string[];
  MarketAccountGroups?: string[];
  MarketAccountTypes?: string[];
  Currencies?: string[];
  BasePairs?: string[];
  Series?: string[];
  Events?: string[];
}

export function colIDToFilterBuilderKey(id: string): keyof PositionsTableFilter | undefined {
  switch (id as keyof Position | ExtraPositionColumns | ExtraUnifiedPositionColumns) {
    case 'SubAccountType':
      return 'SubAccountTypes';
    case 'AssetType':
      return 'AssetTypes';
    case 'Asset':
      return 'Symbols';
    case 'instrument':
      return 'Symbols';
    case 'PositionSource':
      return 'Source';
    case 'SubAccount':
      return 'SubAccounts';
    case 'expiry':
      return 'Expiries';
    case 'MarketAccount':
      return 'MarketAccounts';
    case 'market':
      return 'Markets';
    case 'marketAccountGroup':
      return 'MarketAccountGroups';
    case 'predictionSeries':
      return 'Series';
    case 'predictionEvent':
      return 'Events';
  }
  return undefined;
}

export type ClonePositionsTabFunc = () => void;

export const SubAccountPositionsBlotterMode = {
  Standard: 'Standard',
  Advanced: 'Advanced',
} as const;
export type SubAccountPositionsBlotterMode =
  (typeof SubAccountPositionsBlotterMode)[keyof typeof SubAccountPositionsBlotterMode];

export interface UpdatePositionForm {
  updateType?: LedgerUpdateTypeEnum;
  subAccount?: string;
  marketAccount?: string;
  asset?: string;
  amount?: string;
  avgCost?: string;
  avgCostCurrency?: string;
  timestamp?: Date;
  comments?: string;
}
